/**
 * Cloudflare Stream renders a thumbnail for any frame of an encoded video from
 * one URL — `.../thumbnails/thumbnail.jpg?time=14s` — on demand, with no
 * re-encode and no API call. Picking a reel's poster is therefore only a
 * matter of composing that URL, which is what this does.
 *
 * This mirrors `functions/lib/thumbnails.js`, the way
 * `constants/analyticsTiers.js` mirrors `functions/analytics/schema.js`: the
 * functions package is not importable from the browser bundle, and the two
 * sides must agree exactly, since the server composes what is published and
 * this side composes what the editor previews. Both are unit-tested against
 * the same cases — change one, change the other.
 */

/**
 * @param {?string} baseUrl Cloudflare's own thumbnail URL (`cf.thumbnailUrl`).
 * @param {?number} seconds The chosen frame, or null/undefined for none.
 * @returns {?string} The thumbnail URL, or null when there is no base URL.
 */
export function thumbnailAt(baseUrl, seconds) {
    if (!baseUrl) return null
    if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return baseUrl
    // One decimal: fine enough to pick a frame, coarse enough that the URL is a
    // stable cache key rather than a new one on every pixel of slider travel.
    // Rounded DOWN, never up: Cloudflare answers a `time` past the end of the
    // video with a 400 and no image, so a reel whose poster sits near the end
    // must not have its last tenth rounded over the edge.
    const t = Math.floor(seconds * 10) / 10
    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}time=${t}s`
}

/**
 * A reel's chosen poster frame, or null when it has none.
 *
 * Null and 0 are different answers, and the difference is the whole point:
 * null means nobody chose, so Cloudflare serves the frame
 * `thumbnailTimestampPct` picked when the copy was requested — 10% in — while
 * 0 means someone chose the first frame. Never reach for `reel.thumbTime ||
 * something`; it collapses the two.
 * @param {?object} reel A reel document.
 * @returns {?number} The chosen frame in seconds, or null.
 */
export function thumbTimeOf(reel) {
    const t = reel?.thumbTime
    return typeof t === 'number' && Number.isFinite(t) && t >= 0 ? t : null
}

/**
 * How long a reel runs, for sizing the slider.
 *
 * Cloudflare's figure is the encoded copy's and is authoritative; the
 * document's own is whatever the browser measured at upload time and is absent
 * on reels uploaded before that was recorded. A zero from either is not a
 * usable range, so it falls through.
 * @param {?object} reel A reel document.
 * @returns {?number} Duration in seconds, or null when unknown.
 */
export function reelDurationOf(reel) {
    const usable = (v) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null)
    return usable(reel?.cf?.duration) ?? usable(reel?.duration)
}
